import { FieldType } from './field-models';

/**
 * Gets property value regardless of case sensitivity
 * @param obj The object to get property from
 * @param propName The property name in camelCase format
 * @param defaultValue Default value if property not found
 */
export function getPropCaseInsensetive<T, K extends string, D = null>(
    obj: T | null | undefined,
    propName: K,
    defaultValue: D = null,
): D {
    if (!obj) return defaultValue;

    const objRecord = obj as Record<string, unknown>;

    // Try direct access first
    if (objRecord[propName] !== undefined) {
        return objRecord[propName] as D;
    }

    // Try lowercase version
    const lowerProp = propName.toLowerCase();
    if (objRecord[lowerProp] !== undefined) {
        return objRecord[lowerProp] as D;
    }

    return defaultValue;
}


export function isStringValue(value: unknown): value is string | null | undefined {
    return typeof value === 'string' || value === null || value === undefined;
}

export function isNumberValue(value: unknown): value is number | null | undefined {
    return typeof value === 'number' || value === null || value === undefined;
}

export function isBooleanValue(value: unknown): value is boolean | null | undefined {
    return typeof value === 'boolean' || value === null || value === undefined;
}

export function isDateValue(value: unknown): value is Date | string | null | undefined {
    return value instanceof Date || typeof value === 'string' || value === null || value === undefined;
}

export function isSelectValue(value: unknown): value is string | number | boolean | null | undefined {
    return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null || value === undefined;
}

/**
 * Creates a parameterized translation message for use with TranslateSmartPipe
 * This utility function can be used throughout the location card system for any
 * translation that requires dynamic parameter injection.
 *
 * @param key The translation key (e.g., 'LOCATION_CARD_DYNAMIC_TRANSLATION.RETURN_TO_ACTIVE')
 * @param params Object containing parameters for the translation
 * @returns JSON string that the TranslateSmartPipe can parse and process
 */
export function createSmartTranslationMessage(key: string, params: { [key: string]: number | string | boolean }): string {
    return JSON.stringify({ key, params });
}

/**
 * Type-safe field value assignment utility
 * Assigns a value to a field only if types are compatible
 */
export function convertDate(apiDate: string, dateFormat: string, timeFormat: string): string {
    if (!apiDate) {
        return '';
    }

    const format = dateFormat?.toUpperCase() || 'MM/DD/YYYY';
    const tFormat = timeFormat?.toUpperCase() || 'HH:MM:SS';

    const dateObj = new Date(apiDate);
    const y = dateObj.getFullYear();
    let m = '' + (dateObj.getMonth() + 1);
    let d = '' + dateObj.getDate();
    let h = dateObj.getHours();
    let mm = dateObj.getMinutes().toString();

    if (m.length < 2) m = '0' + m;
    if (d.length < 2) d = '0' + d;
    if (mm.length < 2) mm = '0' + mm;

    let datePart = '';
    if (format === 'MM/DD/YYYY') {
        datePart = m + '/' + d + '/' + y;
    } else if (format === 'YYYY/MM/DD') {
        datePart = y + '/' + m + '/' + d;
    } else if (format === 'DD/MM/YYYY') {
        datePart = d + '/' + m + '/' + y;
    }

    let timePart = '';
    if (tFormat === 'H:MM:SS TT') {
        const ampm = h >= 12 ? 'pm' : 'am';
        h = h % 12;
        h = h ? h : 12;
        timePart = h + ':' + mm + ' ' + ampm;
    } else {
        timePart = h + ':' + mm;
    }

    return datePart + ' ' + timePart;
}

export function roundToDecimal(value: number | undefined | null, decimals: number): number | undefined | null {
    if (value == null) return value;
    const factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
}

export function calculateMaintenanceInterval(maintenanceEnd: string): number {
    if (!maintenanceEnd) {
        return 2;
    }

    const returnToActive = new Date(maintenanceEnd).getTime() - new Date().getTime();
    const hours = Math.floor(returnToActive / 1000 / 60 / 60);

    return hours < 1 ? 1 : hours;
}

export function assignFieldValue(field: import('../config.models').NormalizedFieldState, value: unknown): void {
    if (!field) return;

    switch (field.type) {
        case FieldType.Text:
            if (field.type === FieldType.Text && isStringValue(value)) {
                field.value = value;
            }
            break;
        case FieldType.Number:
            if (field.type === FieldType.Number && isNumberValue(value)) {
                field.value = value;
            }
            break;
        case FieldType.Select:
            if (field.type === FieldType.Select && isSelectValue(value)) {
                field.value = value;
            }
            break;
        case FieldType.Toggle:
            if (field.type === FieldType.Toggle && isBooleanValue(value)) {
                field.value = value;
            }
            break;
        case FieldType.Date:
            if (field.type === FieldType.Date && isDateValue(value)) {
                field.value = value;
            }
            break;
        case FieldType.Autocomplete:
            if (field.type === FieldType.Autocomplete && isStringValue(value)) {
                field.value = value;
            }
            break;
        case FieldType.Checkbox:
            if (field.type === FieldType.Checkbox && isBooleanValue(value)) {
                field.value = value;
            }
            break;
        case FieldType.Range:
            if (field.type === FieldType.Range && isNumberValue(value)) {
                field.value = value;
            }
            break;
        default:
            // For UI and Section fields, or if types don't match
            break;
    }
}